import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';
import { getCharacterImageUrl } from '../../utils/mediaUrl';

const ageGroupOptions = ['18-30', '30-50', '50-75', '75+'];
const genderPresentationOptions = ['Male', 'Female'];
const ethnicAppearanceOptions = [
  'Caucasian',
  'South Asian',
  'East Asian',
  'Black - African',
  'Black - Caribbean',
  'Middle Eastern',
  'Mixed'
];
const bodyHabitusOptions = ['Underweight', 'Normal', 'Overweight', 'Obese'];

const CharacterManager = () => {
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState({
    open: false,
    character: null,
    isEditing: false
  });
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    loadCharacters();
  }, []);

  const loadCharacters = async () => {
    try {
      setLoading(true);
      const response = await api.get('/platform/characters');
      setCharacters(response.data);
    } catch (err) {
      console.error('Error loading characters:', err);
      setError('Failed to load characters');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setDialog({
      open: true,
      character: {
        name: '',
        addressableKey: '',
        iconKey: '',
        description: '',
        ageGroup: '',
        genderPresentation: '',
        ethnicAppearance: '',
        bodyHabitus: ''
      },
      isEditing: false
    });
    setSelectedFile(null);
  };

  const openEditDialog = (character) => {
    setDialog({
      open: true,
      character: {
        ...character,
        ageGroup: character.ageGroup || '',
        genderPresentation: character.genderPresentation || '',
        ethnicAppearance: character.ethnicAppearance || '',
        bodyHabitus: character.bodyHabitus || ''
      },
      isEditing: true
    });
    setSelectedFile(null);
  };

  const closeDialog = () => {
    setDialog({ open: false, character: null, isEditing: false });
    setSelectedFile(null);
    setError('');
  };

  const convertImageToPng = (file, maxDimension = 800) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      const img = new Image();
      img.onload = () => {
        const largestSide = Math.max(img.width, img.height) || 1;
        const scale = Math.min(1, maxDimension / largestSide);
        const targetWidth = Math.max(1, Math.round(img.width * scale));
        const targetHeight = Math.max(1, Math.round(img.height * scale));

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        canvas.toBlob((blob) => {
          if (!blob) {
            reject(new Error('Could not process image'));
            return;
          }
          const nextName = file.name.replace(/\.[^/.]+$/, '') + '.png';
          resolve(new File([blob], nextName, { type: 'image/png', lastModified: Date.now() }));
        }, 'image/png');
      };
      img.onerror = () => reject(new Error('Unable to read image for compression'));
      img.src = evt.target.result;
    };
    reader.onerror = () => reject(new Error('Unable to read image file'));
    reader.readAsDataURL(file);
  });

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) { // 5MB
        setError('File size must be less than 5MB');
        return;
      }

      try {
        const compressed = await convertImageToPng(file, 900);
        setSelectedFile(compressed);
        setError('');
      } catch (conversionError) {
        console.error('Character thumbnail processing failed:', conversionError);
        setError(conversionError.message || 'Failed to process image. Please try another file.');
        setSelectedFile(null);
      } finally {
        event.target.value = '';
      }
    }
  };

  const saveCharacter = async () => {
    try {
      setSaving(true);
      setError('');

      // Validate required fields
      if (!dialog.character.name || !dialog.character.addressableKey || !dialog.character.iconKey) {
        setError('Please fill in all required fields');
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('name', dialog.character.name);
      formData.append('addressableKey', dialog.character.addressableKey);
      formData.append('iconKey', dialog.character.iconKey);
      if (dialog.character.description) {
        formData.append('description', dialog.character.description);
      }
      if (dialog.character.ageGroup) {
        formData.append('ageGroup', dialog.character.ageGroup);
      }
      if (dialog.character.genderPresentation) {
        formData.append('genderPresentation', dialog.character.genderPresentation);
      }
      if (dialog.character.ethnicAppearance) {
        formData.append('ethnicAppearance', dialog.character.ethnicAppearance);
      }
      if (dialog.character.bodyHabitus) {
        formData.append('bodyHabitus', dialog.character.bodyHabitus);
      }
      if (selectedFile) {
        formData.append('profilePicture', selectedFile);
      }

      const method = dialog.isEditing ? 'put' : 'post';
      const url = dialog.isEditing 
        ? `/platform/characters/${dialog.character._id}`
        : '/platform/characters';

      await api[method](url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      closeDialog();
      loadCharacters();
    } catch (err) {
      console.error('Error saving character:', err);
      setError(err.response?.data?.message || 'Failed to save character');
    } finally {
      setSaving(false);
    }
  };

  const deleteCharacter = async (characterId) => {
    if (!window.confirm('Are you sure you want to delete this character? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/platform/characters/${characterId}`);
      loadCharacters();
    } catch (err) {
      console.error('Error deleting character:', err);
      setError('Failed to delete character');
    }
  };

  const updateCharacterField = (field, value) => {
    setDialog({
      ...dialog,
      character: { ...dialog.character, [field]: value }
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Character Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreateDialog}
          sx={{ 
            bgcolor: '#16a085',
            '&:hover': { bgcolor: '#1abc9c' }
          }}
        >
          Add Character
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {characters.map((character) => (
          <Grid item xs={12} sm={6} md={4} key={character._id}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {character.profilePicture && (
                <CardMedia
                  component="img"
                  image={getCharacterImageUrl(character.profilePicture)}
                  alt={character.name}
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    objectFit: 'cover',
                  }}
                />
              )}
              {!character.profilePicture && (
                <Box
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    bgcolor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ImageIcon sx={{ fontSize: 64, color: 'grey.500' }} />
                </Box>
              )}
              
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="h6" gutterBottom>
                  {character.name}
                </Typography>
                
                {character.description && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {character.description}
                  </Typography>
                )}
                
                <Box mt={2}>
                  <Chip 
                    label={`Addressable: ${character.addressableKey}`} 
                    size="small" 
                    sx={{ mb: 1, display: 'block' }}
                  />
                  <Chip 
                    label={`Icon: ${character.iconKey}`} 
                    size="small" 
                    sx={{ mb: 1, display: 'block' }}
                  />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {character.ageGroup && (
                      <Chip label={`Age ${character.ageGroup}`} size="small" color="primary" variant="outlined" />
                    )}
                    {character.genderPresentation && (
                      <Chip label={character.genderPresentation} size="small" variant="outlined" />
                    )}
                    {character.ethnicAppearance && (
                      <Chip label={character.ethnicAppearance} size="small" variant="outlined" />
                    )}
                    {character.bodyHabitus && (
                      <Chip label={character.bodyHabitus} size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
                
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  Created: {format(new Date(character.createdAt), 'MMM dd, yyyy')}
                </Typography>
              </CardContent>
              
              <CardActions>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => openEditDialog(character)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => deleteCharacter(character._id)}
                >
                  Delete
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {characters.length === 0 && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No characters found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Add characters that instructors can use in their cases
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
          >
            Add Your First Character
          </Button>
        </Box>
      )}

      {/* Character Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialog.isEditing ? 'Edit Character' : 'Add New Character'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Character Name"
                value={dialog.character?.name || ''}
                onChange={(e) => updateCharacterField('name', e.target.value)}
                required
                helperText="Display name for this character"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Addressable Key"
                value={dialog.character?.addressableKey || ''}
                onChange={(e) => updateCharacterField('addressableKey', e.target.value)}
                required
                helperText="Unity addressable asset key (e.g., male_young)"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Icon Key"
                value={dialog.character?.iconKey || ''}
                onChange={(e) => updateCharacterField('iconKey', e.target.value)}
                required
                helperText="Unity addressable icon key (e.g., male_young_icon)"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Age Group</InputLabel>
                <Select
                  label="Age Group"
                  value={dialog.character?.ageGroup || ''}
                  onChange={(e) => updateCharacterField('ageGroup', e.target.value)}
                >
                  <MenuItem value="">Not set</MenuItem>
                  {ageGroupOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  label="Gender"
                  value={dialog.character?.genderPresentation || ''}
                  onChange={(e) => updateCharacterField('genderPresentation', e.target.value)}
                >
                  <MenuItem value="">Not set</MenuItem>
                  {genderPresentationOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Ethnic Appearance</InputLabel>
                <Select
                  label="Ethnic Appearance"
                  value={dialog.character?.ethnicAppearance || ''}
                  onChange={(e) => updateCharacterField('ethnicAppearance', e.target.value)}
                >
                  <MenuItem value="">Not set</MenuItem>
                  {ethnicAppearanceOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Body Habitus</InputLabel>
                <Select
                  label="Body Habitus"
                  value={dialog.character?.bodyHabitus || ''}
                  onChange={(e) => updateCharacterField('bodyHabitus', e.target.value)}
                >
                  <MenuItem value="">Not set</MenuItem>
                  {bodyHabitusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                value={dialog.character?.description || ''}
                onChange={(e) => updateCharacterField('description', e.target.value)}
                helperText="Optional description of this character"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Profile Picture
              </Typography>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="profile-picture-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="profile-picture-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                  sx={{ mb: 1 }}
                >
                  {selectedFile ? 'Change Picture' : 'Upload Picture'}
                </Button>
              </label>
              
              {selectedFile && (
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}
              
              <Typography variant="caption" display="block" color="text.secondary">
                Supported formats: JPEG, PNG, GIF (max 5MB). Images are compressed to PNG automatically.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button 
            onClick={saveCharacter} 
            variant="contained"
            disabled={saving}
          >
            {saving ? 'Saving...' : (dialog.isEditing ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CharacterManager;
